import * as React from 'react'

import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'

import { ExternalLinkIcon, IconButton, Skeleton } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import { useCommunityConfig } from '../../CommunityConfig'
import { useMentionConversation } from '../../hooks/mentions/useMentionConversation'
import {
  MentionContextProvider,
  useMentionData,
} from '../context/MentionContext'
import type { MentionData } from '../context/MentionContext'

import { MentionPostMiniature } from '../MentionPostMiniature'

import { useMentionInteraction } from './hooks/useMentionInteraction'
import { useMentionState } from './hooks/useMentionState'
import { MentionActions } from './MentionActions'
import {
  MentionThreadContent,
  MentionThreadContentSkeleton,
  MentionThreadContent_Channel,
  MentionThreadContent_Mention,
} from './MentionThreadContent'
import type { MentionThreadContentChannel } from './MentionThreadContent'
import type { MentionedChannel } from '../../util/getMentionedChannelTextParts'
import { MentionsContextSection } from '../MentionsContextSection'
import { NewItemHighlight } from '../../components/NewItemHighlight'

import styles from './MentionCard.module.css'

import type { JSX } from 'react'

export const MentionCard_Channel = graphql(
  /* GraphQL */ `
    fragment MentionCard_Channel on Channel @_unmask {
      service
      ...MentionThreadContent_Channel
    }
  `,
  [MentionThreadContent_Channel],
)

export const MentionCard_Mention = graphql(
  /* GraphQL */ `
    fragment MentionCard_Mention on Mention @_unmask {
      id
      mentionType
      text
      publicUrl
      author {
        name
        username
      }
      ...MentionThreadContent_Mention
    }
  `,
  [MentionThreadContent_Mention],
)

/** @see MentionThreadContentChannel for why this is still an intersection. */
type MentionCardChannel = FragmentOf<typeof MentionCard_Channel> &
  MentionThreadContentChannel

/**
 * Parent-post miniature above full-context comment mentions. Same expand
 * animation as CommentCard. The thread root `post` is itself a
 * `mentionType: post` mention, so it renders through the same
 * `MentionPostMiniature` path as a post-type card — wrap it in its own
 * provider and let the miniature read it via `useMentionData`.
 */
const AnimatedMentionPostMiniature = ({
  post,
  channel,
}: {
  post: MentionData
  channel: MentionedChannel
}): JSX.Element => {
  return (
    <m.div
      key="post-miniature"
      data-testid="mention-top-miniature-wrapper"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{
        duration: 0.2,
        ease: [0.15, 0, 0.35, 1],
      }}
      className={styles.postMiniatureWrapper}
    >
      <MentionContextProvider mention={post}>
        <MentionPostMiniature channel={channel} />
      </MentionContextProvider>
    </m.div>
  )
}

/** Opens the mention on-platform in a new tab when parent context isn't shown inline. */
const MentionContextLink = ({
  href,
  authorLabel,
}: {
  href: string
  authorLabel: string
}): JSX.Element => {
  return (
    <IconButton
      as="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      label={`Open ${authorLabel}'s mention in a new tab`}
      variant="tertiary"
      size="small"
      className={styles.contextLink}
      data-testid="mention-context-link"
      // The link lives inside `MentionWrapper` (role="button"), whose click
      // handler would otherwise toggle the card's expanded state. Stop the
      // click here so activating the link doesn't also expand the card.
      // Keyboard activation needs no such guard: the wrapper only self-expands
      // on Enter/Space when it is itself the focus target, so the link's own
      // activation already passes through.
      onClick={(e) => e.stopPropagation()}
    >
      <ExternalLinkIcon />
    </IconButton>
  )
}

export const getMentionCardAriaLabel = (
  username: string | null | undefined,
  text: string | null | undefined,
): string =>
  `Mention from ${username ?? 'an account'}: ${
    text?.slice(0, 100) ?? 'No text'
  }${text && text.length > 100 ? '...' : ''}`

type MentionCardViewProps = {
  mentionId: string
  expanded: boolean
  dismissed: boolean
  resolved: boolean
  dimmed: boolean
  ariaLabel: string
  onClick?: (e: React.MouseEvent) => void
  onKeyDown?: (event: React.KeyboardEvent) => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  highlight?: React.ReactNode
  contextLink?: React.ReactNode
  miniature?: React.ReactNode
  actions?: React.ReactNode
  children: React.ReactNode
}

export const MentionCardView = React.forwardRef<
  HTMLLIElement,
  MentionCardViewProps
>(
  (
    {
      mentionId,
      expanded,
      dismissed,
      resolved,
      dimmed,
      ariaLabel,
      onClick,
      onKeyDown,
      onMouseEnter,
      onMouseLeave,
      highlight,
      contextLink,
      miniature,
      actions,
      children,
    },
    ref,
  ): JSX.Element => (
    <li
      id={mentionId}
      ref={ref}
      className={styles.card}
      data-mention-wrapper="true"
      data-mention-id={mentionId}
      data-dismissed={dismissed ? 'true' : 'false'}
      data-resolved={resolved ? 'true' : 'false'}
      data-expanded={expanded ? 'true' : 'false'}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <m.div
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- This motion wrapper is a keyboard-operable card control that also contains nested actions.
        role={onClick ? 'button' : 'group'}
        tabIndex={onClick ? 0 : undefined}
        className={styles.wrapper}
        data-testid="mention-wrapper"
        animate={{
          opacity: dimmed ? 0.6 : 1,
          filter: dimmed ? 'grayscale(1)' : 'grayscale(0)',
        }}
        exit={{ opacity: 0 }}
        transition={{
          opacity: { duration: 0.6, ease: [0.15, 0, 0.35, 1] },
          filter: { duration: 0.6, ease: [0.15, 0, 0.35, 1] },
        }}
        onClick={onClick}
        onKeyDown={onKeyDown}
        aria-expanded={onClick ? expanded : undefined}
        aria-label={ariaLabel}
      >
        {highlight}
        {contextLink}
        <AnimatePresence mode="wait">{miniature}</AnimatePresence>
        <div className={styles.threadContent}>{children}</div>
      </m.div>
      {actions}
    </li>
  ),
)

MentionCardView.displayName = 'MentionCardView'

type MentionCardProps = {
  channel: MentionCardChannel
  expanded?: boolean
  onManualExpansion?: (mentionId: string | null) => void
  onExpandNextMention?: (
    fromMentionId?: string,
    onlyRepliable?: boolean,
  ) => void
  onExpandPreviousMention?: () => void
  onLocalResolutionChange?: (mentionId: string, isResolved: boolean) => void
  isLocallyResolved?: boolean
  /** Marks a mention that just arrived via the pill; plays the entrance flash. */
  isNew?: boolean
}

export const MentionCard = React.forwardRef<HTMLLIElement, MentionCardProps>(
  (
    {
      channel,
      expanded = false,
      onManualExpansion,
      onExpandNextMention,
      onExpandPreviousMention,
      onLocalResolutionChange,
      isLocallyResolved = false,
      isNew = false,
    },
    ref,
  ): JSX.Element => {
    const mention = useMentionData()
    const { config } = useCommunityConfig()

    const canReply = config.mentions.canReply(channel, mention.mentionType)
    const hasFullContext = config.mentions.hasFullContext(
      channel,
      mention.mentionType,
    )

    // One fetch on expand powers both the top miniature (`post`) and the
    // context section (`context`). Only comment-type full-context cards have
    // a parent thread to fetch; post-type and partial-context cards render
    // from their own fields and never hit this query.
    const shouldFetchConversation =
      expanded && mention.mentionType === 'comment' && hasFullContext
    const conversation = useMentionConversation(mention.id, {
      enabled: shouldFetchConversation,
    })

    const showTopMiniature =
      shouldFetchConversation && conversation.post !== null

    const showContextLink =
      mention.mentionType === 'comment' &&
      !hasFullContext &&
      canReply &&
      Boolean(mention.publicUrl)

    const state = useMentionState({
      expanded,
      onLocalResolutionChange,
      isLocallyResolved,
    })
    const interaction = useMentionInteraction({
      expanded,
      onManualExpansion,
      onExpandNextMention,
      onExpandPreviousMention,
      onHoverReset: state.handleMouseLeave,
    })

    // The context section and the main mention's upward connector share this
    // flag so the connector only points at a thread that is on screen.
    // Ancestors are conversation-scoped, so a mention with no `parentId` can
    // still have earlier mentions above it.
    const showContextSection =
      expanded && !state.isResolved && conversation.context.length > 0

    return (
      <MentionCardView
        ref={ref}
        mentionId={mention.id}
        expanded={expanded}
        dismissed={state.isDismissed}
        resolved={state.isResolved}
        dimmed={state.isResolved && !state.isHovered}
        ariaLabel={getMentionCardAriaLabel(
          mention.author?.username,
          mention.text,
        )}
        onClick={interaction.handleMentionClick}
        onKeyDown={interaction.handleKeyDown}
        onMouseEnter={state.handleMouseEnter}
        onMouseLeave={state.handleMouseLeave}
        highlight={
          isNew ? <NewItemHighlight kind="mention" itemId={mention.id} /> : null
        }
        contextLink={
          showContextLink && (
            <MentionContextLink
              href={mention.publicUrl}
              authorLabel={
                mention.author?.name ?? mention.author?.username ?? 'this user'
              }
            />
          )
        }
        miniature={
          showTopMiniature &&
          conversation.post && (
            <AnimatedMentionPostMiniature
              post={conversation.post}
              channel={channel}
            />
          )
        }
        actions={
          <MentionActions
            isHovered={state.isHovered}
            isLocallyDismissed={state.isLocallyDismissed}
            isLocallyReplied={state.isLocallyReplied}
            isInterimExpanded={state.isInterimExpanded}
            disabled={state.actionsLoading}
            expanded={expanded}
            onLoadingChange={state.handleActionsLoadingChange}
            onCollapse={interaction.handleCollapse}
            onExpandNextMention={interaction.handleExpandNextFromAction}
            onDismissed={state.handleDismissed}
            onUndismissed={state.handleUndismissed}
          />
        }
      >
        {showContextSection && (
          <MentionsContextSection
            context={conversation.context}
            loading={conversation.loading}
            channel={channel}
          />
        )}
        <MentionThreadContent
          channel={channel}
          expanded={expanded}
          threadPosition={showContextSection ? 'continued' : 'start'}
          onReplied={state.handleReplied}
          onCollapse={interaction.handleCollapse}
          onExpandNextMention={onExpandNextMention}
        />
      </MentionCardView>
    )
  },
)

MentionCard.displayName = 'MentionCard'

export const MentionCardSkeleton = (): JSX.Element => {
  return (
    <li className={styles.card}>
      <div className={styles.wrapper}>
        <MentionThreadContentSkeleton />
      </div>
      <Skeleton width={30} height={30} />
    </li>
  )
}
